/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * CategoryComponent.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import {RouteComponentProps} from 'react-router-dom';
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Category from "../../models/Category";
import UIToast from "../../tools/view/toast/UIToast";
import KareviHTTPService from "../../tools/service/KareviHTTPService";
import FormSection from "../../tools/view/form/FormSection";
import VideoService from "../../services/VideoService";
import Video from "../../models/Video";
import PageComponent from "../../tools/view/layout/PageComponent";
import FormInput, {Type} from "../../tools/view/form/FormInput";
import {TextField} from "../../tools/model/form/TextField";
import CategoryService from "../../services/CategoryService";
import VideoMetaDataForm from "../../models/form/video/VideoMetaDataForm";
import {VideoMetaDataDTO} from "../../models/dto/video/VideoMetaDataDTO";
import {AbstractForm, FormListener} from "../../tools/model/form/AbstractForm";
import Colors from "../../tools/model/enumerations/Colors";
import NavigationService from "../../tools/service/NavigationService";
import UIError from "../../tools/view/toast/UIError";
import EmptyForm from "../../models/form/EmptyForm";
import VideoFileForm from "../../models/form/video/VideoFileForm";
import {VideoFileUploadDTO} from "../../models/dto/video/VideoFileUploadDTO";
import PageTab from "../../tools/view/layout/PageTab";
import VideoCatalogSelectForm from "../../models/form/video/VideoCatalogSelectForm";
import CatalogForm from "../../models/form/catalog/CatalogForm";
import {CatalogDTO} from "../../models/dto/catalog/CatalogDTO";
import {VideoCatalogSelectDTO} from "../../models/dto/video/VideoCatalogSelectDTO";
import Spinner from "../../tools/view/element/Spinner";

interface Props extends RouteComponentProps<MatchParams> {
}

interface MatchParams {
    categoryId: string;
    videoId: string;
}

interface State {
    policy: Policy;
    category?: Category;
    video?: Video;
    videoForm: VideoMetaDataForm;
    catalogForm: VideoCatalogSelectForm;
    deleteForm: EmptyForm;
    form360: VideoFileForm;
    form720: VideoFileForm;
    formThumbnail: VideoFileForm;
    isSavingCatalogs: boolean;
}

export enum VideoFileType {
    VIDEO_360 = "VIDEO_360",
    VIDEO_720 = "VIDEO_720",
    FILE_THUMBNAIL = "THUMBNAIL"
}

export default class VideoPage extends HiloReactComponent<Props, State> implements PolicyListener, FormListener<VideoMetaDataForm, VideoMetaDataDTO>, FormListener<CatalogForm, CatalogDTO>, FormListener<VideoFileForm, VideoFileUploadDTO> {

    private readonly _categoryService: CategoryService;
    private readonly _videoService: VideoService;
    private readonly _httpService: KareviHTTPService;
    private readonly _navigationService: NavigationService;
    private _modalContainer?: Element;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: Props, state: State) {
        super(props, state);

        this._categoryService = this._serviceRepository.resolve(CategoryService.id) as CategoryService
        this._videoService = this._serviceRepository.resolve(VideoService.id) as VideoService
        this._httpService = this._serviceRepository.resolve(KareviHTTPService.id) as KareviHTTPService
        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService

        this.state = {
            policy: this._policyService.policy,
            category: undefined,
            isSavingCatalogs: false,
            videoForm: new VideoMetaDataForm(),
            catalogForm: new VideoCatalogSelectForm([], this.props.match.params.videoId, this._serviceRepository),
            deleteForm: new EmptyForm(),
            form360: new VideoFileForm(VideoFileType.VIDEO_360),
            form720: new VideoFileForm(VideoFileType.VIDEO_720),
            formThumbnail: new VideoFileForm(VideoFileType.FILE_THUMBNAIL)
        };
    }

    /**
     * onUpdate
     */
    public onUpdate(): void {
        this.forceUpdate();
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._policyService.addListener(this);

        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this.state.videoForm.addListener(this)
        this.state.form360.addListener(this)
        this.state.form720.addListener(this)
        this.state.formThumbnail.addListener(this)
        this.state.catalogForm.addListener(this)

        this._refreshCategory()
    }

    /**
     * _refreshCategory
     */
    private _refreshCategory = async () => {
        let category = await this._categoryService.findById(this.props.match.params.categoryId);

        await this.setState({category: category});

        this._refreshVideo()
    };

    /**
     * _refreshVideo
     */
    private _refreshVideo = async () => {
        if (!this.state.category) return

        try {
            let video = await this._videoService.findById(this.state.category, this.props.match.params.videoId);

            await this.setState({video: video})
            await this.state.videoForm.load(VideoMetaDataDTO.fromVideo(video));
            await this.state.formThumbnail.load(VideoFileUploadDTO.fromVideo(video, VideoFileType.FILE_THUMBNAIL, this._videoService))
            await this.state.form360.load(VideoFileUploadDTO.fromVideo(video, VideoFileType.VIDEO_360, this._videoService))
            await this.state.form720.load(VideoFileUploadDTO.fromVideo(video, VideoFileType.VIDEO_720, this._videoService))
            await this.forceUpdate()
        } catch (e) {
            console.error(e)
        }
    };

    public componentWillUnmount(): void {
        this.state.videoForm.removeListener(this);
        this.state.form720.removeListener(this);
        this.state.form360.removeListener(this);
        this.state.formThumbnail.removeListener(this);
        this._policyService.removeListener(this);
        super.componentWillUnmount();
    }

    /**
     * componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.match.params.categoryId !== this.props.match.params.categoryId) {
            this._refreshCategory();
        } else if (prevProps.match.params.videoId !== this.props.match.params.videoId) {
            this._refreshVideo();
        }
    }


    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    private async _deleteVideo() {
        const {video} = this.state;

        if (!window.confirm(this.t.videoRemoveConfirmation)) return;

        if (!video) return;

        try {
            await this._videoService.removeVideo(video)

            this._navigationService.goToParentPage()
        } catch (e) {
            UIError.default.show(e)
            console.error(e);
        }
    }

    private _handleCatalogFormSubmission = () => {
        const catalogToAddIds = this.state.catalogForm.getCatalogsWhereVideoNeedToBeAdded();
        const catalogToRemoveIds = this.state.catalogForm.getCatalogsWhereVideoNeedToBeRemoved();

        console.error("catalogAddIds: ", catalogToAddIds);
        console.error("catalogRemIds: ", catalogToRemoveIds);

        if (this.state.isSavingCatalogs) return;

        this.setState({
            isSavingCatalogs: true
        }, async () => {
            for (let catalogId of catalogToAddIds) {
                    await this._videoService.assignVideoToCatalog(this.props.match.params.videoId, catalogId);
            }

            for (let catalogId of catalogToRemoveIds) {
                await this._videoService.removeVideoFromCatalog(this.props.match.params.videoId, catalogId);
            }

            this.setState({isSavingCatalogs: false});
        })

    }

    /**
     * _videoView
     * @returns {any}
     * @private
     */
    private _videoView = (props: {
        metaDataForm: VideoMetaDataForm,
        catalogForm: VideoCatalogSelectForm,
        deleteForm: EmptyForm,
        form360: VideoFileForm,
        form720: VideoFileForm,
        formThumbnail: VideoFileForm,
        category?: Category
    }) => {
        let videoIdField = new TextField("video_id", t => "Video ID", _ => true, Type.TEXT, false, this.props.match.params.videoId, "", true)

        return <>
            <FormInput field={videoIdField}/>
            {/*<FormSection label={this.t.catalogs} form={} submit={}/>*/}

            <FormSection label={this.t.information} form={props.metaDataForm} submit={this._updateVideo}/>
            <FormSection label={this.t.catalogAvailability} form={props.catalogForm}
                         submit={this._handleCatalogFormSubmission}>
                {this.state.isSavingCatalogs && <Spinner/>}
            </FormSection>

            <FormSection label={this.t.video360} form={props.form360}
                         submit={(payload) => this._submitFile(payload, VideoFileType.VIDEO_360)}/>
            <FormSection label={this.t.video720} form={props.form720}
                         submit={(payload) => this._submitFile(payload, VideoFileType.VIDEO_720)}/>
            <FormSection label={this.t.thumbnail} form={props.formThumbnail}
                         submit={(payload) => this._submitFile(payload, VideoFileType.FILE_THUMBNAIL)}/>
            <FormSection label={this.t.deleteVideo}
                         form={props.deleteForm}
                         submit={dto => this._deleteVideo()}
                         submitBtn={{color: Colors.red, labelAccessor: t => t.deleteVideoButton}}/>
        </>
    };

    /**
     * _saveVideo
     * @param dto
     * @private
     */
    private _updateVideo = async (dto: Map<string, any> | FormData) => {
        const {video, videoForm} = this.state;
        if (!video) return console.log("category or video is undefined.");

        try {
            await this._videoService.updateVideo(video, dto);

            videoForm.statusCode = 200;
            this.setState({
                videoForm: videoForm
            })
            this.state.videoForm.statusCode = 200;
            UIToast.default.show(this.t.videoUpdated)
            await this._refreshCategory();
            await this._refreshVideo();
        } catch (e) {
            videoForm.statusCode = e.status;
            this.setState({
                videoForm: videoForm
            })
        }
    };

    /**
     * _saveVideo
     * @param dto
     * @private
     */
    private _assignVideoToCatalogs = async (dto: Map<string, any>) => {
        const {video, videoForm} = this.state;
        if (!video) return console.log("category or video is undefined.");

        try {
            await this._videoService.updateVideo(video, dto);

            videoForm.statusCode = 200;
            this.setState({
                videoForm: videoForm
            })
            this.state.videoForm.statusCode = 200;
            UIToast.default.show(this.t.videoUpdated)
            await this._refreshCategory();
            await this._refreshVideo();
        } catch (e) {
            videoForm.statusCode = e.status;
            this.setState({
                videoForm: videoForm
            })
        }
    };

    /**
     * onFormUpdate
     * @param form
     */
    onFormUpdate = (form: AbstractForm<VideoMetaDataDTO>
        | AbstractForm<VideoFileUploadDTO>
        | AbstractForm<VideoCatalogSelectDTO>) => {
        if (form instanceof VideoMetaDataForm) {
            this.setState({videoForm: form})
        } else if (form instanceof VideoFileForm) {
            switch (form.fileType) {
                case VideoFileType.FILE_THUMBNAIL:
                    this.setState({formThumbnail: form})
                    break;
                case VideoFileType.VIDEO_360:
                    this.setState({form360: form})
                    break;
                case VideoFileType.VIDEO_720:
                    this.setState({form720: form})
                    break;
            }
        } else if (form instanceof CatalogForm) {

        }
    };

    /**
     * _submitFile
     * @param payload
     * @param fileType
     * @returns {Promise<void>}
     * @private
     */
    private async _submitFile(payload: Map<string, any> | FormData, fileType: VideoFileType) {
        const {formThumbnail, form720, form360, video} = this.state;
        let form: AbstractForm<any> | undefined = undefined;
        console.log("Start upload sequence.")

        if (!video) return

        switch (fileType) {
            case VideoFileType.VIDEO_360:
                form = form360;
                break;
            case VideoFileType.VIDEO_720:
                form = form720;
                break;
            case VideoFileType.FILE_THUMBNAIL:
                form = formThumbnail;
                break;
        }

        if (!form) return;

        try {
            await this._videoService.uploadFile(video, fileType, payload, form instanceof VideoFileForm ? form : undefined);

            form.statusCode = 200;
            this.setState(this.state)

            UIToast.default.show(this.t.videoUpdated)
            await this._refreshCategory();
            await this._refreshVideo();
        } catch (e) {
            form.statusCode = e.status;
            this.setState(this.state)
            console.error(e);
        }

        console.log("End upload sequence")
    }

    /**
     * render
     */
    public render(): any {
        const {category, videoForm, catalogForm, deleteForm, form360, form720, formThumbnail, video} = this.state;
        const titleTabs: TitleTab[] = [
            {
                title: t => t.details, content: <PageTab>
                    <this._videoView metaDataForm={videoForm}
                                     catalogForm={catalogForm}
                                     deleteForm={deleteForm}
                                     form360={form360}
                                     form720={form720}
                                     formThumbnail={formThumbnail}
                                     category={category}/>
                </PageTab>
            },
        ];

        return <PageComponent>
            <PageTitle title={t => t.videoSettings}
                       tabs={titleTabs}
                       subTitle={video?.title || this.t.loading}
                       backLabel={() => category?.title || "Retour"}
            />
            {titleTabs[0].content}
        </PageComponent>
    }
}
